import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import Presale from './components/Presale';
import Dashboard from './components/Dashboard';
import Stake from './components/Stake';
import Login from './components/Login';
import Register from './components/Register';
import TransactionDetail from './components/TransactionDetail';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import Profile from './components/Profile';
import LeadBoard from './components/LeadBoard';
import AdminDashboard from './components/AdminDashboard';
import AdminTransactionDetail from './components/AdminTransactionDetail';
import PresaleAgreement from './components/PresaleAgreement';
import Contact from './components/Contact'; // Add this line
import './styles.css';

// ProtectedRoute component to handle authentication
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null); // Initial state as null to avoid premature redirects
  const [isAdmin, setIsAdmin] = useState(false);

  const checkAuth = () => {
    const token = localStorage.getItem('token'); 
    if (!token) {
      setIsAuthenticated(false);
      setIsAdmin(false);
      return;
    }

    try {
      const decoded = jwtDecode(token);
      if (decoded.exp < Date.now() / 1000) {
        console.log('Token expired, redirecting to login');
        localStorage.removeItem('token');
        setIsAuthenticated(false);
        setIsAdmin(false);
        return;
      }
      setIsAuthenticated(true);
      setIsAdmin(decoded.is_admin === 1);
    } catch (err) {
      console.error('Error decoding JWT:', err);
      localStorage.removeItem('token');
      setIsAuthenticated(false);
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    // Initial check with a slight delay to allow localStorage to update
    setTimeout(() => {
      checkAuth();
    }, 100);

    // Listen for storage changes (e.g., logout from another tab)
    window.addEventListener('storage', checkAuth);

    // Check token periodically (every 10 seconds)
    const interval = setInterval(checkAuth, 10000);

    return () => {
      window.removeEventListener('storage', checkAuth);
      clearInterval(interval);
    };
  }, []);

  // Show loading state while authentication is being determined
  if (isAuthenticated === null) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    console.log('Not authenticated, redirecting to /login');
    return <Navigate to="/login" />;
  }

  if (adminOnly && !isAdmin) {
    console.log('Not an admin, redirecting to /presale');
    return <Navigate to="/presale" />;
  }

  return children;
};

const App = () => {
  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/presale-agreement" element={<PresaleAgreement />} />
        <Route path="/contact" element={<Contact />} /> {/* Add this line */}

        {/* Protected Routes */}
        <Route path="/presale" element={<ProtectedRoute><Presale /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/stake" element={<ProtectedRoute><Stake /></ProtectedRoute>} />
        <Route path="/transaction-detail" element={<ProtectedRoute><TransactionDetail /></ProtectedRoute>} />

        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/leadboard" element={<ProtectedRoute><LeadBoard /></ProtectedRoute>} />
        {/* Admin Routes */}
        <Route path="/adminDashboard" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin-transaction-detail" element={<ProtectedRoute adminOnly><AdminTransactionDetail /></ProtectedRoute>} />

        {/* Root Route */}
        <Route path="/" element={<ProtectedRoute><Navigate to="/dashboard" /></ProtectedRoute>} />

        {/* Fallback Route */}
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    </Router>
  );
};

export default App;
